import React from "react";
import {
  useFeaturedProducts,
  useOnSaleProducts,
  useRecentProducts,
  usePopularProducts,
  type Product,
} from "../../hooks/products";

import ProductSkeleton from "./ProductSkeleton";
import ErrorState from "./ErrorState";
import { ProductGrid } from "../_ResponsiveComponents";
import ViewAllButton from "./ViewAllButton";
// import { useWishlistContext } from "../../hooks/useWishlistContext";
import SectionHeader from "./SectionHeader";

interface UnifiedProductsProps {
  sortType: "newest" | "popular" | "on_sale" | "featured";
  categoryId?: number | null;
  subCategoryId?: number | null;
  limitCount?: number;
  title?: string;
  subtitle?: string;
  onAddToCart?: (product: Product) => void;
  onQuickView?: (product: Product) => void;
  onViewAll?: () => void;
  viewAllLabel?: string;
  showViewAllButton?: boolean;
}

const UnifiedProducts: React.FC<UnifiedProductsProps> = ({
  sortType,
  limitCount = 36,
  title = "",
  subtitle = "",
  // onAddToCart,
  // onQuickView,
  onViewAll,
  viewAllLabel,
  showViewAllButton = true,
}) => {
  // Use the new unified hooks - they return standardized results
  let products: Product[],
    isLoading: boolean,
    error: Error | null,
    refetch: () => void;

  switch (sortType) {
    case "newest":
      ({ products, isLoading, error, refetch } = useRecentProducts(limitCount));
      break;
    case "popular":
      ({ products, isLoading, error, refetch } =
        usePopularProducts(limitCount));
      break;
    case "on_sale":
      ({ products, isLoading, error, refetch } = useOnSaleProducts(limitCount));
      break;
    case "featured":
      ({ products, isLoading, error, refetch } =
        useFeaturedProducts(limitCount));
      break;
    default:
      ({ products, isLoading, error, refetch } = useRecentProducts(limitCount));
  }

  // Products are already transformed by the new hooks with standardized fields!
  // No need for manual transformation anymore
  const normalizedProducts: any[] = products?.map((item) => ({
    ...item,
    sale_rate: item.sale_rate ?? undefined, // Convert null to undefined for compatibility
    sortType, // Just add the sortType for UI display
  }));

  // Get wishlist data from context
  // const { wishlistedIds } = useWishlistContext();

  // Determine the path for the View All button based on sortType
  const getViewAllPath = () => {
    switch (sortType) {
      case "newest":
        return "/products?sort=newest";
      case "on_sale":
        return "/products?sort=on_sale";
      case "popular":
        return "/products?sort=popular";
      case "featured":
        return "/products?sort=featured";
      default:
        return "/products";
    }
  };

  if (isLoading) return <ProductSkeleton count={limitCount} />;
  if (error)
    return (
      <ErrorState
        error={error.message || "Failed to load products"}
        onRetry={refetch || (() => {})}
      />
    );
  if (!products || products.length === 0) return null;

  return (
    <div>
      <SectionHeader title={title} subtitle={subtitle} sortType={sortType}>
        {/* View All Button */}
        {showViewAllButton && (onViewAll || products.length >= limitCount) && (
          <ViewAllButton
            onClick={onViewAll}
            label={viewAllLabel}
            sortType={sortType}
            path={getViewAllPath()}
          />
        )}
      </SectionHeader>

      {/* Products Grid */}
      <ProductGrid
        products={normalizedProducts || []}
        viewMode="grid"
        loading={isLoading || false}
        is_HScroll={true}
        isShowWishlist={true}
      />
    </div>
  );
};

export default UnifiedProducts;
